import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PASSWORD_REGEX } from '@skytech/shared';

import { api, ApiError } from '../../lib/api';
import { InlineSpinner } from '../../components/ui/Spinner';

function Shell({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="mx-auto max-w-md px-6 py-16">
      <div className="rounded-lg border border-frost bg-white p-8 shadow-card">
        <h1 className="text-xl font-light text-navy-900">{title}</h1>
        {children}
      </div>
    </div>
  );
}

/** SRS §6.2 — request a reset link. */
export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    // The endpoint always 200s (no account enumeration), so we always show success.
    try {
      await api.post('/auth/forgot-password', { email });
    } finally {
      setSent(true);
      setBusy(false);
    }
  }

  return (
    <Shell title="Reset your password">
      {sent ? (
        <div className="mt-4">
          <p className="text-sm text-navy-700">
            If <strong>{email}</strong> is registered, a reset link is on its way. It expires in 30 minutes.
          </p>
          <Link to="/login" className="mt-4 inline-block text-sm text-sky-ink hover:underline">← Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-4">
          <p className="text-sm text-neutral">Enter your email and we'll send a link to reset your password.</p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.co.ke"
            className="w-full rounded-md border border-frost px-3 py-2.5 text-sm focus:border-sky-500"
          />
          <button type="submit" disabled={busy} aria-busy={busy || undefined} className="flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 py-3 text-sm font-medium text-white disabled:opacity-50">
            {busy && <InlineSpinner />}
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
          <Link to="/login" className="block text-center text-sm text-sky-ink hover:underline">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

/** SRS §6.2 — set a new password from the emailed token. */
export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!token) {
    return (
      <Shell title="Invalid link">
        <p className="mt-3 text-sm text-neutral">This reset link is missing its token. Request a new one.</p>
        <Link to="/forgot-password" className="mt-4 inline-block text-sm text-sky-ink hover:underline">Request a new link</Link>
      </Shell>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!PASSWORD_REGEX.test(password)) {
      setError('Password needs 8+ characters with an uppercase letter, a lowercase letter, and a digit.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      navigate('/login?reset=1');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reset your password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="mt-4 space-y-4">
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
          placeholder="New password"
          className="w-full rounded-md border border-frost px-3 py-2.5 text-sm focus:border-sky-500"
        />
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          autoComplete="new-password"
          placeholder="Confirm new password"
          className="w-full rounded-md border border-frost px-3 py-2.5 text-sm focus:border-sky-500"
        />
        {error && <p className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">{error}</p>}
        <button type="submit" disabled={busy} aria-busy={busy || undefined} className="flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 py-3 text-sm font-medium text-white disabled:opacity-50">
          {busy && <InlineSpinner />}
          {busy ? 'Saving…' : 'Reset password'}
        </button>
      </form>
    </Shell>
  );
}
